import { TwitchStreamsService } from '../lib/twitchStreams';
import { twitchService } from '../lib/twitch';
import { raidQueueService } from '../lib/raidQueue';
import Swal from 'sweetalert2';
import './ChannelDetailsModal.css';

export class ChannelDetailsModal {
  private static instance: ChannelDetailsModal;
  private modal: HTMLElement;
  private streamsService?: TwitchStreamsService;

  private constructor() {
    this.modal = this.createModal();
    document.body.appendChild(this.modal);
  }

  public static getInstance(): ChannelDetailsModal {
    if (!ChannelDetailsModal.instance) {
      ChannelDetailsModal.instance = new ChannelDetailsModal();
    }
    return ChannelDetailsModal.instance;
  }

  private createModal(): HTMLElement {
    const modal = document.createElement('div');
    modal.className = 'modal-overlay';
    
    // Create modal content
    modal.innerHTML = `
      <div class="modal-content">
        <button class="modal-close">&times;</button>
        <div class="modal-header">
          <div class="channel-header">
            <img class="channel-image" src="" alt="Channel Profile">
            <div class="channel-info">
              <h2 class="channel-name"></h2>
              <div class="channel-meta">
                <div class="stream-status"></div>
                <div class="meta-separator">•</div>
                <div class="follower-count"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-body">
          <div class="channel-description"></div>
        </div>
        <div class="modal-footer">
          <div class="channel-actions">
            <button class="modal-button raid-button" disabled>Raid Channel</button>
            <button class="modal-button close-button">Close</button>
          </div>
        </div>
      </div>
    `;

    // Add scroll indicator functionality
    const modalBody = modal.querySelector('.modal-body') as HTMLElement;
    const checkScroll = () => {
      if (modalBody.scrollHeight > modalBody.clientHeight) {
        modalBody.classList.add('scrollable');
      } else {
        modalBody.classList.remove('scrollable');
      }
    };

    // Check on content changes
    const observer = new MutationObserver(checkScroll);
    observer.observe(modalBody, { 
      childList: true, 
      subtree: true, 
      characterData: true 
    });

    // Check on window resize
    window.addEventListener('resize', checkScroll);

    // Close button handlers
    const closeButtons = modal.querySelectorAll('.modal-close, .close-button');
    closeButtons.forEach(button => {
      button?.addEventListener('click', () => this.hide());
    });

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.hide();
      }
    });

    // Initial scroll check
    setTimeout(checkScroll, 100);

    return modal;
  }

  private initializeStreamsService(): TwitchStreamsService {
    const clientId = import.meta.env.VITE_TWITCH_CLIENT_ID;
    const accessToken = twitchService.getAccessToken();
    if (!clientId || !accessToken) {
      throw new Error('Twitch credentials not available');
    }
    return new TwitchStreamsService(clientId, accessToken);
  }

  public async show(channelId: string): Promise<void> {
    try {
      // Initialize TwitchStreamsService if not already initialized
      if (!this.streamsService) {
        this.streamsService = this.initializeStreamsService();
      }

      // Get user, channel, and follower data
      const [userData, userResponse, channelResponse] = await Promise.all([
        this.streamsService.getUserById(channelId),
        fetch(`https://api.twitch.tv/helix/users?id=${channelId}`, {
          headers: {
            'Authorization': `Bearer ${twitchService.getAccessToken()}`,
            'Client-Id': import.meta.env.VITE_TWITCH_CLIENT_ID
          }
        }).then(res => res.json()),
        fetch(`https://api.twitch.tv/helix/channels/followers?broadcaster_id=${channelId}`, {
          headers: {
            'Authorization': `Bearer ${twitchService.getAccessToken()}`,
            'Client-Id': import.meta.env.VITE_TWITCH_CLIENT_ID
          }
        }).then(res => res.json())
      ]);

      const followerCount = channelResponse.total || 0;
      const channelData = userResponse.data?.[0];

      if (!userData) {
        throw new Error('Failed to fetch channel data');
      }

      // Get stream data
      const streamData = await this.streamsService.getStreamByUserId(channelId);

      // Update modal content
      const profileImg = this.modal.querySelector('.channel-image') as HTMLImageElement;
      profileImg.src = userData.profile_image_url;
      profileImg.alt = `${userData.display_name}'s profile`;

      const nameEl = this.modal.querySelector('.channel-name') as HTMLElement;
      nameEl.textContent = userData.display_name;

      const followerEl = this.modal.querySelector('.follower-count') as HTMLElement;
      followerEl.textContent = `${new Intl.NumberFormat().format(followerCount)} followers`;

      const statusEl = this.modal.querySelector('.stream-status') as HTMLElement;
      const descEl = this.modal.querySelector('.channel-description') as HTMLElement;

      // Update status
      if (streamData) {
        statusEl.textContent = '🔴 Live Now';
        statusEl.style.color = '#ff4444';
      } else {
        statusEl.textContent = '⚫ Offline';
        statusEl.style.color = '#999';
      }

      // Update description
      descEl.textContent = channelData?.description || 'This channel has no description yet.';

      // Configure raid button
      const raidButton = this.modal.querySelector('.raid-button') as HTMLButtonElement;
      raidButton.disabled = !streamData;
      raidButton.onclick = async () => {
        try {
          if (this.streamsService) {
            // Add confirmation dialog
            const result = await Swal.fire({
              title: 'Start Raid?',
              html: `Are you sure you want to raid <strong>${userData.display_name}</strong>?`,
              icon: 'question',
              showCancelButton: true,
              confirmButtonText: 'Yes, Start Raid!',
              cancelButtonText: 'Cancel',
              confirmButtonColor: '#9147ff',
              cancelButtonColor: '#6e7681',
              background: '#18181b',
              color: '#ffffff',
              width: '360px'
            });

            if (result.isConfirmed) {
              // Modify button to show that raid is starting
              raidButton.textContent = 'Starting Raid...';
              raidButton.disabled = true;
              
              // Start the raid
              await this.streamsService.raidChannel(channelId);
              raidQueueService.markAsRaided(channelId);
              
              // Show success message
              await Swal.fire({
                title: 'Raid Started!',
                html: `Successfully started raid to <strong>${userData.display_name}</strong>`,
                icon: 'success',
                timer: 2000,
                timerProgressBar: true,
                confirmButtonColor: '#9147ff',
                background: '#18181b',
                color: '#ffffff',
                width: '360px'
              });
              
              this.hide();
            }
          }
        } catch (error) {
          console.error('Failed to start raid:', error);
          
          // Show error message
          const errorMessage = error instanceof Error ? error.message : 'Unable to start the raid. Please try again.';
          await Swal.fire({
            title: 'Unable to Start Raid',
            html: `<div style="text-align: left; margin-top: 1rem;">${errorMessage}</div>`,
            icon: 'error',
            confirmButtonColor: '#9147ff',
            background: '#18181b',
            color: '#ffffff',
            width: '360px'
          });
          
          // Reset button
          raidButton.textContent = 'Raid Channel';
          raidButton.disabled = false;
        }
      };

      // Show modal
      this.modal.classList.add('active');
    } catch (error) {
      console.error('Error showing channel details:', error);
    }
  }

  public hide(): void {
    this.modal.classList.remove('active');
  }
}

// Export singleton instance
export const channelDetailsModal = ChannelDetailsModal.getInstance();

// Expose to window for console access
declare global {
  interface Window {
    showChannelDetails: (channelId: string) => Promise<void>;
  }
}

window.showChannelDetails = (channelId: string) => channelDetailsModal.show(channelId);
